import React, { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { StatusBadge, PersonaBadge } from "@/components/Badges";
import { Clock, Lock, ExternalLink, Loader2, Trash2, Waves } from "lucide-react";

const COLUMNS = [
  { key: "Awaiting Execution", label: "READY" },
  { key: "Verification Failed", label: "FAILED · REDO" },
  { key: "Completed", label: "DONE" },
];

function fmtCountdown(iso) {
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "unlocked";
  const m = Math.floor(ms / 60000);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  return `${m}m`;
}

export default function AgentDashboard() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["agent-tasks"],
    queryFn: () => api.get("/agent/tasks").then(r => r.data),
    refetchInterval: 15000,
  });

  const delDrift = useMutation({
    mutationFn: (id) => api.delete(`/tasks/${id}`),
    onSuccess: () => { toast.success("Drift task removed"); qc.invalidateQueries({ queryKey: ["agent-tasks"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const grouped = useMemo(() => {
    const out = { "Awaiting Execution": [], "Verification Failed": [], "Completed": [] };
    (q.data?.tasks || []).forEach(t => {
      if (out[t.status]) out[t.status].push(t);
    });
    return out;
  }, [q.data]);

  return (
    <Layout title="Task Board." subtitle="Only unlocked tasks are shown">
      {q.isLoading && <div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div>}

      {/* Locked notice */}
      {q.data?.locked_count > 0 && (
        <div className="border border-white/10 px-4 py-3 mb-6 flex items-center gap-3 bg-[#0a0a0c]" data-testid="locked-notice">
          <Lock className="w-4 h-4 text-amber-400"/>
          <div className="font-mono text-[12px] text-zinc-400">
            {q.data.locked_count} task{q.data.locked_count > 1 ? "s" : ""} time-locked. Next unlock:{" "}
            <span className="text-white">
              {fmtCountdown(q.data.locked?.[0]?.unlocks_at)}
            </span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {COLUMNS.map(col => (
          <div key={col.key} className="border border-white/10 min-h-[400px]" data-testid={`kanban-col-${col.key.replace(/\s/g,'-').toLowerCase()}`}>
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">{col.label}</div>
              <div className="font-mono text-[11px] text-zinc-500">{grouped[col.key].length}</div>
            </div>
            <div className="p-3 space-y-3">
              {grouped[col.key].length === 0 && (
                <div className="text-[12px] font-mono text-zinc-600 py-8 text-center">— empty —</div>
              )}
              {grouped[col.key].map(t => {
                const isDrift = t.source === "persona_drift";
                return (
                <div key={t.id} className="relative">
                  <Link
                    to={`/agent/tasks/${t.id}`}
                    data-testid={`task-card-${t.id}`}
                    className={`block border p-3 transition-colors bg-[#09090B] ${
                      col.key === "Verification Failed" ? "border-[#FF3B30]/50 hover:border-[#FF3B30] scanlines" : "border-white/10 hover:border-white/25"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1.5">
                        <PersonaBadge persona={t.persona_angle}/>
                        {isDrift && (
                          <span data-testid={`drift-badge-${t.id}`} className="text-[9px] font-mono uppercase tracking-widest border border-[#007AFF]/50 text-[#007AFF] px-1.5 py-0.5 flex items-center gap-1">
                            <Waves className="w-2.5 h-2.5"/> drift
                          </span>
                        )}
                      </div>
                      <StatusBadge status={t.status}/>
                    </div>
                    <div className="text-[13px] font-medium text-white mb-1">{t.action_type}</div>
                    <div className="text-[12px] text-zinc-400 line-clamp-2 min-h-[2.5em]">
                      {t.task_script || <em className="text-zinc-600">action-only — no script</em>}
                    </div>
                    <div className="mt-2 pt-2 border-t border-white/5 flex items-center justify-between gap-2">
                      <a href={t.target_url} target="_blank" rel="noreferrer" onClick={(e)=>e.stopPropagation()} className="font-mono text-[10px] text-[#FF3B30] hover:underline flex items-center gap-1 truncate">
                        <ExternalLink className="w-3 h-3"/> {t.subreddit}
                      </a>
                      <div className="font-mono text-[10px] text-zinc-500 flex items-center gap-1">
                        <Clock className="w-3 h-3"/> +{t.execution_delay_minutes}m
                      </div>
                    </div>
                    {col.key === "Verification Failed" && t.verification_note && (
                      <div className="mt-2 pt-2 border-t border-[#FF3B30]/30 font-mono text-[10px] text-[#FF3B30]">
                        // {t.verification_note}
                      </div>
                    )}
                  </Link>
                  {isDrift && col.key === "Awaiting Execution" && (
                    <button
                      onClick={(e) => {
                        e.preventDefault(); e.stopPropagation();
                        if (window.confirm("Delete this drift task? This is only for benign daily tasks you don't want to do.")) {
                          delDrift.mutate(t.id);
                        }
                      }}
                      disabled={delDrift.isPending}
                      data-testid={`self-delete-drift-${t.id}`}
                      title="Skip this drift task"
                      className="absolute top-2 right-2 p-1 border border-white/10 hover:border-[#FF3B30] bg-[#050505] text-zinc-500 hover:text-[#FF3B30] disabled:opacity-50"
                    >
                      <Trash2 className="w-3 h-3"/>
                    </button>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );
}
